<template>
  <div>
    <lb-datatablenew
      data-testid="gl-selection-table"
      :headers="tableHeaders"
      :items="processedItems"
      :enableslot="enabledSlots"
      hide-default-footer
      :items-per-page="-1"
      :loading="false"
      height="70vh"
      fixed-header
      dense
      class="v-table-color"
      :itemClass="getRowClass"
    >
      <template v-slot:nav="{ item }">
        <v-skeleton-loader v-if="loading" type="text" width="80%" class="my-2" />
        <div v-else-if="item.__type === 'ungrouped_header'" class="tree-cell cursor-pointer" @click.stop="ungroupedAccordionExpanded = !ungroupedAccordionExpanded"></div>
        <div v-else-if="item.__isUngrouped" class="tree-cell" data-testid="gl-selection-table__ungrouped-nav-cell">
          <div class="tree-node-wrapper">
            <div class="tree-expand-icon">
              <span class="expand-icon">
                <v-icon small class="leaf-node-icon" color="primary">mdi-checkbox-blank</v-icon>
              </span>
            </div>
          </div>
        </div>
        <div v-else @click="handleToggleExpand(item)" data-testid="gl-selection-table__nav-cell" class="cursor-pointer tree-cell">
          <div class="tree-node-wrapper">
            <div class="tree-connectors" v-if="item.level > 0">
              <template v-for="(connector, idx) in getTreeConnectors(item, processedItems.indexOf(item))">
                <div 
                  :key="'connector-' + idx"
                  class="tree-connector-segment"
                  :class="[
                    'tree-level-' + connector.level,
                    {
                      'has-vertical-line': connector.showVerticalLine || connector.isCurrentLevel,
                      'is-current-level': connector.isCurrentLevel,
                      'is-last-child': connector.isLastChild,
                      'is-first-child': connector.isFirstChild,
                      'is-inverted': connector.isInverted
                    }
                  ]"
                  :style="{ left: ((connector.level - 1) * 20 + 10) + 'px' }"
                >
                  <div 
                    class="vertical-line" 
                    v-if="connector.showVerticalLine || connector.isCurrentLevel"
                    :class="{ 
                      'half-height': connector.isCurrentLevel && connector.isLastChild
                    }"
                  ></div>
                  <div 
                    class="horizontal-branch" 
                    v-if="connector.isCurrentLevel"
                  ></div>
                </div>
              </template>
            </div>
            <div class="tree-expand-icon" :style="{ marginLeft: (item.level * 20) + 'px' }">
              <span class="expand-icon">
                <template v-if="hasChildren(item)">
                  <v-icon small>
                    {{ isExpanded(item) ? "mdi-minus-box" : "mdi-plus-box" }}
                  </v-icon>
                  <div v-if="isExpanded(item)" class="parent-child-line" :class="['tree-level-' + (item.level + 1)]"></div>
                </template>
                <template v-else>
                  <v-icon small class="leaf-node-icon" color="primary">mdi-checkbox-blank</v-icon>
                </template>
              </span>
            </div>
            <!-- Line extending from expanded parent to children - color based on child level -->
            <div v-if="isExpanded(item) && hasChildren(item)" class="parent-child-line" :class="['tree-level-' + (item.level + 1)]" :style="{ left: ((item.level * 20) + 9) + 'px' }"></div>
          </div>
        </div>
      </template>

      <template v-slot:code="{ item }">
        <v-skeleton-loader v-if="loading" type="text" width="80%" class="my-2" />
        <div
          v-else-if="item.__type === 'ungrouped_header'"
          class="d-flex align-center ungrouped-header-content cursor-pointer"
          @click.stop="ungroupedAccordionExpanded = !ungroupedAccordionExpanded"
          @keydown.enter.stop.prevent="ungroupedAccordionExpanded = !ungroupedAccordionExpanded"
          @keydown.space.stop.prevent="ungroupedAccordionExpanded = !ungroupedAccordionExpanded"
          tabindex="0"
          role="button"
          :aria-expanded="ungroupedAccordionExpanded.toString()"
          data-testid="gl-selection-table__ungrouped-accordion-header"
        >
          <v-icon small class="mr-2">{{ ungroupedAccordionExpanded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
          <span class="font-weight-bold text-subtitle-2">Ungrouped Items</span>
          <v-chip x-small class="ml-2" color="grey lighten-2">{{ ungroupedItems.length }}</v-chip>
        </div>
        <div v-else class="d-flex align-center justify-space-between" data-testid="gl-selection-table__code-cell">
          <span class="ml-2" data-testid="gl-selection-table__code-value">{{ item.code }} - {{ item.name }}
          </span>
          <img v-if="item.sub_ledger_exists === true" src="/img/icons/DrillDown.svg" alt="Sl Icon" class="cursor-pointer" @click="openDrillDown(item)" />
        </div>
      </template>



      <template v-slot:gl_selection="{ item }">
        <div v-if="item.__type === 'ungrouped_header'"></div>
        <div v-else class="d-flex justify-center" data-testid="gl-selection-table__gl-checkbox-cell">
          <v-skeleton-loader v-if="loading" type="text" width="90%" class="my-2" />
          <v-checkbox v-else data-testid="gl-selection-table__gl-checkbox" class="selecedcheckbox" hide-details
            :input-value="isGlSelected(item.code)"
            @click.stop.prevent="handleGlCheckboxClick($event, item.code)"
            dense></v-checkbox>
        </div>
      </template>

      <template v-slot:gl_status="{ item }">
        <div v-if="item.__type === 'ungrouped_header'"></div>
        <div v-else data-testid="gl-selection-table__gl-status-cell">
          <v-skeleton-loader v-if="loading" type="text" width="80%" class="my-2" />
          <template v-else>
            <StatusIndicator
              v-if="showStatusGLSSet.has(item.code)"
              :variant="STATUS_VARIANTS.APPROVED"
              custom-label="Enabled"
              data-testid="gl-selection-table__gl-status-indicator"
            />
            <StatusIndicator
              v-else
              :variant="STATUS_VARIANTS.DISABLED"
              custom-label="Disabled"
              data-testid="gl-selection-table__gl-status-indicator"
            />
          </template>
        </div>
      </template>

      <template v-slot:sl_selection="{ item }">
        <div v-if="item.__type === 'ungrouped_header'"></div>
        <div v-else class="d-flex justify-center" data-testid="gl-selection-table__sls-checkbox-cell">
          <v-skeleton-loader v-if="loading" type="text" width="90%" class="my-2" />
          <v-checkbox v-else-if="localGlSelectedSet.has(item.code) && item.sub_ledger_exists" data-testid="gl-selection-table__sls-checkbox" class="selecedcheckbox" hide-details
            :input-value="isSlSelected(item.code)"
            @click.stop.prevent="handleSlCheckboxClick($event, item.code)"
            dense></v-checkbox>
        </div>
      </template>

      <template v-slot:sl_status="{ item }">
        <div v-if="item.__type === 'ungrouped_header'"></div>
        <div v-else data-testid="gl-selection-table__sls-status-cell">
          <v-skeleton-loader v-if="loading" type="text" width="80%" class="my-2" />
          <template v-else>
            <StatusIndicator
              v-if="showStatusSLSSet.has(item.code) && item.sub_ledger_exists"
              :variant="STATUS_VARIANTS.APPROVED"
              custom-label="Enabled"
              data-testid="gl-selection-table__sls-status-indicator"
            />
            <StatusIndicator
              v-else-if="item.sub_ledger_exists"
              :variant="STATUS_VARIANTS.DISABLED"
              custom-label="Disabled"
              data-testid="gl-selection-table__sls-status-indicator"
            />
          </template>
        </div>
      </template>

      <template v-slot:badges="{ item }">
        <div v-if="item.__type === 'ungrouped_header'"></div>
        <div v-else data-testid="gl-selection-table__badges-cell">
          <v-skeleton-loader v-if="loading" type="chip" width="90%" class="my-1" />
          <template v-else>
            <v-chip v-if="item.glnew" label color="info" class="white--text mr-1" data-testid="gl-selection-table__new-badge">
              <span>New</span>
            </v-chip>
            <v-chip color="primary" v-if="item.subsequent_children?.length"><span v-if="item.subsequent_children?.length" >{{ (item.subsequent_children?.length || '-') }} GL</span>
            </v-chip> 
            <v-chip class="ml-1" color="primary" v-if="item.sub_ledger_exists"><span>1 SL</span></v-chip>
          </template>
        </div>
      </template>
    </lb-datatablenew>

    <lb-dialog
      v-if="drillDownDialog"
      v-model="drillDownDialog"
      heading="Drill-Down Details"
      width="520"
    >
      <template v-slot:body>
        <div class="drilldown-container">
          
          <!-- Row 1 -->
          <div class="drilldown-row">
            <div class="drilldown-label">
              GL Code &amp; Description
            </div>
            <div class="drilldown-value">
              {{ selectedDrillDownItem.code }} - {{ selectedDrillDownItem.name }}
            </div>
          </div>

          <!-- Row 2 -->
          <div class="drilldown-row">
            <div class="drilldown-label">
              Drill-Down Name
            </div>
            <div class="drilldown-value">
              {{ selectedDrillDownItem.name }}
            </div>
          </div>

        </div>
      </template>
    </lb-dialog>

  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import StatusIndicator from '../../components/StatusIndicator.vue';
import { STATUS_VARIANTS } from '../../utils/statusConstants';

export default {
  name: 'GlSelectionTable',
  components: {
    StatusIndicator
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    visibleItems: {
      type: Array,
      required: true,
      default: () => []
    },
    selectedgls: {
      type: Array,
      required: true,
      default: () => []
    },
    selectedsls: {
      type: Array,
      required: true,
      default: () => []
    },
    savedGlsStatus: {
      type: Array,
      default: () => []
    },
    savedSlsStatus: {
      type: Array,
      default: () => []
    },
    expandedIds: {
      type: Array,
      required: true,
      default: () => []
    },
    glLabel: {
      type: String,
      default: 'GL'
    },
    headers: {
      type: Array,
      default: () => ['Navigation', 'Code', 'GL Selection', 'GL Status', 'Drill-down Selection', 'Drill-down Status', 'Count']
    },
    ungroupedItems: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // Local copies of selections - initialized from props and kept in sync
      localSelectedGls: [],
      localSelectedSls: [],
      STATUS_VARIANTS,
      drillDownDialog: false,
      selectedDrillDownItem: {},
      ungroupedAccordionExpanded: false
    }
  },
  created() {
    // Initialize local state from props
    this.localSelectedGls = [...(this.selectedgls || [])];
    this.localSelectedSls = [...(this.selectedsls || [])];

    // NON-REACTIVE CACHE: These bypass Vue's reactivity system entirely.
    // Checkbox clicks update these instantly without triggering any re-render.
    this._glCache = new Set(this.selectedgls || []);
    this._slCache = new Set(this.selectedsls || []);
    this._emitTimer = null;
  },
  beforeDestroy() {
    clearTimeout(this._emitTimer);
    this._glCache = null;
    this._slCache = null;
  },
  computed: {
    ...mapGetters(['isGlNavigationEnabled']),
    enabledSlots() {
      const slots = ['code', 'gl_selection', 'gl_status', 'sl_selection', 'sl_status', 'badges'];
      // Navigation tree column is only relevant when GL is NOT flattened
      if (!this.isGlNavigationEnabled) {
        slots.unshift('nav');
      }
      return slots;
    },
    tableHeaders() {
      const headerTexts = this.headers || [];
      const navHeader = { text: headerTexts[0] || 'Navigation', value: 'nav', sortable: false };
      // Mirror template condition: nav column only shown when tree structure is present
      const base = this.isGlNavigationEnabled ? [] : [navHeader];
      return [
        ...base,
        { text: headerTexts[1] || 'Code', value: 'code', sortable: false, cellClass: 'gl-code-description-cell' },
        { text: headerTexts[2] || 'GL Selection', value: 'gl_selection', sortable: false },
        {
          text: headerTexts[3] || 'GL Status',
          value: 'gl_status',
          sortable: false,
          filterable: true,
          filterList: ['Enabled', 'Disabled']
        },
        { text: headerTexts[4] || 'Drill-down Selection', value: 'sl_selection', sortable: false, align: 'center' },
        {
          text: headerTexts[5] || 'Drill-down Status',
          value: 'sl_status',
          sortable: false,
          filterable: true,
          filterList: ['Enabled', 'Disabled']
        },
        { text: headerTexts[6] || 'Count', value: 'badges', sortable: false }
      ];
    },
    showStatusGLSSet() {
      return new Set(this.savedGlsStatus || []);
    },
    showStatusSLSSet() {
      return new Set(this.savedSlsStatus || []);
    },
    localGlSelectedSet() {
      return new Set(this.localSelectedGls);
    },
    processedItems() {
      if (this.loading) {
        return Array.from({ length: 10 }, (_, i) => ({
          code: `loading-${i}`,
          name: '',
          level: 0,
          loading: true
        }));
      }
      const glsSet = this.showStatusGLSSet;
      const slsSet = this.showStatusSLSSet;
      const mainItems = this.visibleItems.map(item => ({
        ...item,
        gl_status: glsSet.has(item.code) ? 'Enabled' : 'Disabled',
        sl_status: (slsSet.has(item.code) && item.sub_ledger_exists) ? 'Enabled' : 'Disabled'
      }));
      if (!this.ungroupedItems || this.ungroupedItems.length === 0) {
        return mainItems;
      }
      const sentinel = {
        __type: 'ungrouped_header',
        code: '__ungrouped_header__',
        _id: '__ungrouped_header__',
        name: '',
        // Arrays match both filter options so the header is never hidden by status filters
        gl_status: ['Enabled', 'Disabled'],
        sl_status: ['Enabled', 'Disabled']
      };
      const result = [...mainItems, sentinel];
      if (this.ungroupedAccordionExpanded) {
        result.push(...this.ungroupedItems.map(item => ({
          ...item,
          __isUngrouped: true,
          gl_status: glsSet.has(item.code) ? 'Enabled' : 'Disabled',
          sl_status: (slsSet.has(item.code) && item.sub_ledger_exists) ? 'Enabled' : 'Disabled'
        })));
      }
      return result;
    }
  },
  watch: {
    // Sync from parent ONLY when genuinely different (prevents feedback loop)
    selectedgls(newVal) {
      const incoming = newVal || [];
      // Skip if this is just our own emit bouncing back
      if (incoming.length === this._glCache.size &&
          incoming.every(c => this._glCache.has(c))) return;
      this._glCache = new Set(incoming);
      this.localSelectedGls = [...incoming];
      this.$forceUpdate(); // Re-render since cache is non-reactive
    },
    selectedsls(newVal) {
      const incoming = newVal || [];
      if (incoming.length === this._slCache.size &&
          incoming.every(c => this._slCache.has(c))) return;
      this._slCache = new Set(incoming);
      this.localSelectedSls = [...incoming];
      this.$forceUpdate();
    }
  },
  methods: {
    getRowClass(item) {
      if (item.__type === 'ungrouped_header') return 'ungrouped-accordion-header cursor-pointer';
      if (item.__isUngrouped) return 'ungrouped-item-row';
      return item.priority === 1 ? 'gl-priority-row' : '';
    },
    // O(1) lookups from NON-REACTIVE cache — zero Vue overhead
    isGlSelected(code) {
      return this._glCache.has(code);
    },
    isSlSelected(code) {
      return this._slCache.has(code);
    },

    /**
     * Debounced sync: batches rapid clicks into a single reactive update + emit.
     * Waits 100ms of inactivity before updating Vue state and notifying parent.
     */
    _scheduleSyncToParent() {
      clearTimeout(this._emitTimer);
      this._emitTimer = setTimeout(() => {
        this.$emit('update:selectedgls', [...this._glCache]);
        this.$emit('update:selectedsls', [...this._slCache]);
      }, 100);
    },

    /**
     * Handle GL checkbox click — ZERO Vue re-renders on click.
     * 1. Update non-reactive cache (instant)
     * 2. Toggle DOM classes (instant visual)
     * 3. Debounced batch sync to parent (100ms)
     */
    handleGlCheckboxClick(event, code) {
      const newValue = !this._glCache.has(code);

      // PHASE 1: Update non-reactive cache (instant, zero overhead)
      if (newValue) {
        this._glCache.add(code);
      } else {
        this._glCache.delete(code);
        // Also remove SL when GL unchecked
        this._slCache.delete(code);
      }

      // Immediately sync localSelectedGls so the reactive localGlSelectedSet computed
      // updates, making the SL column visibility react without a full $forceUpdate().
      this.localSelectedGls = [...this._glCache];
      if (!newValue) this.localSelectedSls = [...this._slCache];

      // PHASE 2: Direct DOM manipulation (instant visual)
      const checkbox = event.target.closest('.v-input--checkbox');
      if (checkbox) {
        const icon = checkbox.querySelector('.v-icon');
        if (newValue) {
          checkbox.classList.add('v-input--is-dirty');
          icon?.classList.remove('mdi-checkbox-blank-outline');
          icon?.classList.add('mdi-checkbox-marked');
        } else {
          checkbox.classList.remove('v-input--is-dirty');
          icon?.classList.remove('mdi-checkbox-marked');
          icon?.classList.add('mdi-checkbox-blank-outline');
        }
      }

      // PHASE 3: Debounced batch sync (100ms after last click)
      this._scheduleSyncToParent();
    },

    /**
     * Handle SL checkbox click — same pattern as GL
     */
    handleSlCheckboxClick(event, code) {
      const newValue = !this._slCache.has(code);

      // PHASE 1: Update non-reactive cache
      if (newValue) {
        this._slCache.add(code);
      } else {
        this._slCache.delete(code);
      }
      this.localSelectedSls = [...this._slCache];

      // PHASE 2: Direct DOM manipulation
      const checkbox = event.target.closest('.v-input--checkbox');
      if (checkbox) {
        const icon = checkbox.querySelector('.v-icon');
        if (newValue) {
          checkbox.classList.add('v-input--is-dirty');
          icon?.classList.remove('mdi-checkbox-blank-outline');
          icon?.classList.add('mdi-checkbox-marked');
        } else {
          checkbox.classList.remove('v-input--is-dirty');
          icon?.classList.remove('mdi-checkbox-marked');
          icon?.classList.add('mdi-checkbox-blank-outline');
        }
      }

      // PHASE 3: Debounced batch sync
      this._scheduleSyncToParent();
    },
    hasChildren(item) {
      return item?.children?.length > 0;
    },
    isExpanded(item) {
      if (!item?.children?.length) return false;
      return this.expandedIds.includes(item.children[0]);
    },
    handleToggleExpand(item) {
      this.$emit('toggle-expand', item);
    },
    handleSelectionChange() {
      // Legacy - no longer used, toggle methods handle this
    },
    handleSelectionSlsChange() {
      // Legacy - no longer used, toggle methods handle this
    },
    openDrillDown(item) {
      this.selectedDrillDownItem = item;
      this.drillDownDialog = true;
    },
    /**
     * Check if item is the last child among its siblings at the same level
     */
    isLastChildAtLevel(item, itemIndex) {
      if (!item || item.level === 0) return false;
      
      const items = this.processedItems;
      const currentLevel = item.level;
      
      // Look forward to find if there's another sibling at the same level
      for (let i = itemIndex + 1; i < items.length; i++) {
        const nextItem = items[i];
        if (!nextItem) continue;
        
        // If we hit an item at a lower level (parent level), we're the last child
        if (nextItem.level < currentLevel) {
          return true;
        }
        // If we hit an item at the same level, we're not the last child
        if (nextItem.level === currentLevel) {
          return false;
        }
        // If we hit an item at a higher level (child of current), continue looking
      }
      
      // If we reached the end, we're the last child
      return true;
    },
    /**
     * Get the tree connector lines that should be shown for this item
     */
    getTreeConnectors(item, itemIndex) {
      if (!item || item.level === 0) return [];
      
      const connectors = [];
      const items = this.processedItems;
      const isInverted = false; // Standard tree
      
      // For each ancestor level, determine if we need a vertical line
      for (let level = 1; level <= item.level; level++) {
        let hasMoreSiblingsAtLevel = false;
        
        // Check forward to see if there are more siblings at this level
        for (let i = itemIndex + 1; i < items.length; i++) {
          const futureItem = items[i];
          if (!futureItem) continue;
          
          // If we hit an item at a lower level (parent level), stop looking
          if (futureItem.level < level) {
            break;
          }
          // If we hit an item at the same level, we need a vertical line to connect to it
          if (futureItem.level === level) {
            hasMoreSiblingsAtLevel = true;
            break;
          }
        }
        
        const isLastChild = level === item.level && this.isLastChildAtLevel(item, itemIndex);
        const isFirstChild = false;
        
        connectors.push({
          level: level,
          showVerticalLine: hasMoreSiblingsAtLevel,
          isCurrentLevel: level === item.level,
          isLastChild: isLastChild,
          isFirstChild: isFirstChild,
          isInverted: isInverted
        });
      }
      
      return connectors;
    }
  }
}
</script>

<style scoped>
/* Tree Cell Styles */
.tree-cell {
  position: relative;
  min-width: 120px;
  overflow: visible;
}

.tree-node-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  min-height: 42px;
}

.tree-connectors {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  height: 100%;
  width: 100%;
  pointer-events: none;
}

/* Tree hierarchy level colors */
.tree-level-1 .vertical-line,
.tree-level-1 .horizontal-branch,
.parent-child-line.tree-level-1 {
  background-color: var(--green-500);
}

.parent-child-line.tree-level-1 {
  left: var(--space-4-5) !important;
}

.tree-level-2 .vertical-line,
.tree-level-2 .horizontal-branch,
.parent-child-line.tree-level-2 {
  background-color: var(--red-500);
}

.tree-level-3 .vertical-line,
.tree-level-3 .horizontal-branch,
.parent-child-line.tree-level-3 {
  background-color: var(--blue-500);
}

.tree-level-4 .vertical-line,
.tree-level-4 .horizontal-branch,
.parent-child-line.tree-level-4 {
  background-color: var(--logo-color3, #6b67ce);
}

.tree-level-5 .vertical-line,
.tree-level-5 .horizontal-branch,
.parent-child-line.tree-level-5 {
  background-color: var(--green-500);
}

.tree-level-6 .vertical-line,
.tree-level-6 .horizontal-branch,
.parent-child-line.tree-level-6 {
  background-color: var(--red-500);
}

.tree-connector-segment {
  position: absolute;
  top: -5px;
  height: 120%;
  width: var(--space-9);
}

.vertical-line {
  position: absolute;
  left: -1px;
  top: 4px;
  height: 100%;
  width: var(--border-md);
}

.vertical-line.half-height {
  height: 58%;
  top: -4px;
}

.horizontal-branch {
  position: absolute;
  left: -1px;
  top: 50%;
  transform: translateY(-50%);
  width: var(--space-sm);
  height: 3px;
  background-color: var(--green-500);
}

.tree-expand-icon {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}

.expand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: var(--space-3xs);
  background-color: var(--grey-white);
  position: relative;
}

.expand-icon .v-icon {
  color: var(--color-primary-base);
}

.parent-child-line {
  position: absolute;
  width: var(--border-md);
  left: var(--space-4-5);
  background-color: var(--color-primary-base);
  top: 50%;
  height: calc(50% + 21px);
}

/* Animations */
@keyframes rayTraceDown {
  0% { clip-path: inset(0 0 100% 0); }
  100% { clip-path: inset(0 0 0 0); }
}

@keyframes rayTraceRight {
  0% { clip-path: inset(0 100% 0 0); }
  100% { clip-path: inset(0 0 0 0); }
}

.parent-child-line {
  animation: rayTraceDown 0.6s ease-out 1 forwards;
  animation-fill-mode: forwards;
}

.tree-connector-segment .vertical-line {
  clip-path: inset(0 0 100% 0);
  animation: rayTraceDown 0.5s ease-out 1 forwards;
  animation-fill-mode: forwards;
}

.tree-connector-segment .horizontal-branch {
  clip-path: inset(0 100% 0 0);
  animation: rayTraceRight 0.4s ease-out 1 forwards;
  animation-fill-mode: forwards;
}

.tree-level-1 .vertical-line { animation-delay: 0.1s; }
.tree-level-1 .horizontal-branch { animation-delay: 0.3s; }
.tree-level-2 .vertical-line { animation-delay: 0.2s; }
.tree-level-2 .horizontal-branch { animation-delay: 0.4s; }
.tree-level-3 .vertical-line { animation-delay: 0.3s; }
.tree-level-3 .horizontal-branch { animation-delay: 0.5s; }
.tree-level-4 .vertical-line { animation-delay: 0.4s; }
.tree-level-4 .horizontal-branch { animation-delay: 0.6s; }

.no-children-spacer {
  display: inline-block;
  width: var(--icon-md);
  height: var(--icon-md);
}

.leaf-node-icon {
  color: var(--color-primary-base) !important;
}

.text-center {
  text-align: center;
}

.selecedcheckbox {
  display: inline-flex;
  margin: 0;
}

.selecedcheckbox::deep .v-input__control {
  justify-content: center;
}

.selecedcheckbox::deep .v-input--selection-controls__input {
  margin: 0;
}

.drilldown-container {
  border: var(--border-sm) solid var(--grey-200);
  border-radius: var(--radius-12);
  overflow: hidden;
  background: var(--grey-white);
}

.drilldown-row {
  display: grid;
  grid-template-columns: 45% 55%;
  border-bottom: var(--border-sm) solid var(--grey-100);
}

.drilldown-row:last-child {
  border-bottom: none;
}

.drilldown-label {
  background-color: var(--grey-75);
  padding: 18px var(--padding-md);
  font-weight: 600;
  color: var(--grey-900);
}

.drilldown-value {
  background-color: var(--grey-white);
  padding: 18px var(--padding-md);
  font-weight: 500;
  color: var(--grey-800);
}
/* Priority GL row styles - bold text and stronger border on GL code cell */
::v-deep(.gl-priority-row) > td {
  border-top: 0.25px solid var(--color-text-primary) !important;
  border-bottom: 0.25px solid var(--color-text-primary) !important;
  border-left: 4px solid var(--color-text-primary) !important;
}
/* Only the last cell gets a right border */
::v-deep(.gl-priority-row) > td:last-child {
  border-right: 0.25px solid var(--color-text-primary) !important;
}
::v-deep(.gl-priority-row) > td:first-child {
  border-radius: var(--radius-4) 0 0 var(--radius-4) !important;
}
/* Suppress top border on consecutive priority rows to prevent 0.25+0.25=0.50px doubling */
::v-deep(.gl-priority-row + .gl-priority-row) > td {
  border-top: none !important;
}
/* Suppress left border on all adjacent table cells in priority rows to prevent right+left border doubling */
::v-deep(.gl-priority-row) > td + td {
  border-left: none !important;
}

/* Ungrouped Accordion Styles */
::v-deep(.ungrouped-accordion-header) {
  background-color: var(--color-bg-light) !important;
  user-select: none;
  transition: background-color var(--duration-med, 300ms) var(--ease-standard, ease);
  cursor: pointer;
}

::v-deep(.ungrouped-accordion-header:hover) {
  background-color: var(--grey-75) !important;
}

::v-deep(.ungrouped-item-row) {
  background-color: var(--grey-weak) !important;
}

.ungrouped-header-content {
  padding: var(--space-3xs) var(--space-xs);
}
</style>